type QuickActionSurface = "chat" | "navigate" | "sticky";

type QuickActionCardOptions = {
  surface: QuickActionSurface;
  isPrototypeMinimalChrome: boolean;
};

type IconButtonOptions = {
  active?: boolean;
};

type PrototypeShellOptions = {
  resolvedIsFullScreen: boolean;
  useBodyOnlyFullPageChrome: boolean;
};

type PrototypeSidebarButtonOptions = {
  active: boolean;
};

type WelcomeSubmitOptions = {
  enabled: boolean;
};

export const SCHOOL_SHELL_ASSISTANT_STYLE_CONTRACT_TEST_IDS = [
  "ai-prototype-shell",
  "ai-prototype-shell-header",
  "ai-prototype-shell-title",
  "ai-prototype-width-button",
  "ai-prototype-model-button",
  "ai-prototype-utility-toggle",
  "ai-prototype-new-conversation",
  "ai-prototype-back-to-quick-actions",
  "ai-prototype-shell-dismiss-full",
  "ai-prototype-shell-dismiss-mobile",
  "ai-prototype-quick-actions-scroll-left",
  "ai-prototype-quick-actions-scroll-right",
  "ai-prototype-chat-pane",
  "ai-prototype-side-pane",
  "ai-prototype-preview-pane",
  "ai-prototype-welcome-view",
  "ai-school-shell-header",
  "ai-school-shell-model-pill",
  "ai-school-shell-context-button",
  "ai-school-shell-route-action-sticky-widget",
  "ai-school-shell-quick-action",
  "ai-school-shell-quick-action-icon",
  "ai-school-shell-recent-item",
] as const;

const assistantControlBase =
  "inline-flex shrink-0 items-center justify-center rounded-full border border-transparent text-text-secondary transition-colors hover:bg-muted hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20";

const assistantControlActive =
  "bg-muted text-primary hover:bg-muted hover:text-primary";

const assistantCardBase =
  "border border-border bg-surface-light text-irongrey shadow-none transition-colors hover:border-primary/60 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20";

const assistantSoftControl =
  "border border-border bg-muted text-text-secondary transition-colors hover:border-primary/60 hover:bg-muted hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20";

export const schoolShellAssistantStyleContract = Object.freeze({
  shellRootClassName: ({
    resolvedIsFullScreen,
    useBodyOnlyFullPageChrome,
  }: PrototypeShellOptions): string =>
    [
      resolvedIsFullScreen
        ? useBodyOnlyFullPageChrome
          ? "relative flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-surface-light text-irongrey"
          : "fixed inset-0 z-[1105] flex min-h-0 flex-col bg-background-light text-irongrey"
        : "relative flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-surface-light text-irongrey",
    ].join(" "),
  floatingActionButtonClassName:
    "flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-soft transition-colors hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
  authorityScopeClassName:
    "min-h-0 flex flex-1 flex-col overflow-hidden bg-surface-light text-irongrey",
  headerClassName:
    "flex h-12 shrink-0 items-center justify-between border-b border-border bg-surface-light px-4 text-irongrey",
  headerTitleButtonClassName:
    "group inline-flex items-center gap-2 border-none bg-transparent p-0 font-heading text-base font-semibold text-irongrey shadow-none ring-0 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-0",
  headerTitleBackIconClassName:
    "pointer-events-none text-text-secondary transition-colors group-hover:text-primary group-focus-visible:text-primary",
  headerIconButtonClassName: ({ active }: IconButtonOptions = {}): string =>
    [
      "flex h-8 w-8 items-center justify-center",
      active ? assistantControlActive : "",
      assistantControlBase,
    ]
      .filter(Boolean)
      .join(" "),
  headerSmallIconButtonClassName: "flex h-7 w-7 items-center justify-center rounded-full text-primary transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
  modelMenuClassName:
    "absolute top-full right-0 z-[70] mt-1 w-[280px] max-w-[calc(100vw-24px)] overflow-hidden rounded-xl border border-border bg-surface-light text-irongrey shadow-soft",
  previewPaneClassName:
    "flex min-h-0 flex-1 flex-col bg-surface-light text-irongrey",
  previewHeaderClassName:
    "flex h-10 items-center gap-2 border-b border-border bg-muted px-4 text-irongrey",
  previewBackButtonClassName:
    "flex h-6 w-6 items-center justify-center rounded text-text-secondary transition-colors hover:bg-background-light hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
  previewTitleClassName: "truncate text-xs font-semibold text-irongrey",
  previewBodyClassName:
    "min-h-0 flex-1 overflow-auto bg-background-light text-irongrey",
  utilityPaneClassName:
    "flex min-h-0 flex-1 flex-col overflow-hidden bg-surface-light text-irongrey",
  utilityTabBarClassName: "flex border-b border-border bg-surface-light",
  utilityTabClassName: (active: boolean): string =>
    [
      "flex w-1/2 items-center justify-center gap-1.5 px-3 py-3 text-center text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
      active
        ? "border-b border-primary bg-muted font-semibold text-primary"
        : "border-b border-transparent bg-surface-light text-primary/70 hover:bg-primary/5 hover:text-primary",
    ].join(" "),
  utilityTrayClassName:
    "min-h-0 flex-1 overflow-y-auto bg-background-light text-irongrey",
  fullSidebarClassName:
    "flex w-14 shrink-0 flex-col items-center gap-1 border-r border-border bg-surface-light py-3 text-irongrey",
  fullSidebarBrandClassName:
    "mb-3 flex h-8 w-8 items-center justify-center rounded-full border-2 border-primary text-[11px] font-bold text-primary",
  fullSidebarButtonClassName: ({ active }: PrototypeSidebarButtonOptions): string =>
    [
      "flex h-9 w-9 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
      active
        ? "bg-primary/10 text-primary"
        : "text-text-secondary hover:bg-muted hover:text-primary",
    ].join(" "),
  welcomeViewClassName:
    "flex h-full min-h-full flex-1 flex-col items-center justify-center bg-background-light px-6 py-8 text-irongrey",
  routeCanvasRootClassName:
    "relative flex h-full min-h-full flex-1 flex-col bg-background-light text-irongrey",
  routeCanvasWelcomeContentClassName:
    "flex h-full min-h-full flex-1 flex-col items-center justify-center px-6 py-8",
  welcomeIconClassName:
    "mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary",
  welcomeHeadingClassName:
    "mb-3 text-center font-heading text-3xl font-bold text-irongrey",
  welcomeMessageClassName:
    "mb-8 max-w-[580px] text-center text-sm leading-6 text-text-secondary",
  welcomeInputShellClassName:
    "relative rounded-2xl border border-border bg-surface-light shadow-soft",
  welcomeInputClassName:
    "max-h-[160px] min-h-[52px] w-full resize-none overflow-y-auto bg-transparent py-4 pl-5 pr-12 text-sm leading-[1.5] text-irongrey outline-none placeholder:text-text-tertiary",
  welcomeAttachClassName:
    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-text-secondary transition-colors hover:bg-background-light hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
  welcomeSubmitClassName: ({ enabled }: WelcomeSubmitOptions): string =>
    [
      "flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
      enabled
        ? "bg-primary text-white hover:bg-primary-hover"
        : "cursor-not-allowed bg-muted text-text-tertiary",
    ].join(" "),
  welcomeModelButtonClassName: ({ active }: IconButtonOptions = {}): string =>
    [
      "flex h-8 w-8 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
      active ? "bg-muted text-primary" : "text-text-secondary hover:bg-muted hover:text-primary",
    ].join(" "),
  routeCanvasStagedFileClassName:
    "inline-flex h-7 items-center gap-1 rounded-lg border border-border bg-surface-light pl-2 pr-2 text-[11px] text-irongrey",
  routeCanvasStagedFileRemoveClassName:
    "ml-0.5 text-text-tertiary transition-colors hover:text-irongrey focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
  routeCanvasModelMenuClassName:
    "fixed w-[288px] max-w-[calc(100vw-24px)] overflow-hidden rounded-xl border border-border bg-surface-light text-irongrey shadow-soft",
  routeCanvasQuickActionCardClassName:
    "group flex items-center gap-3 rounded-xl border border-border bg-surface-light p-3.5 text-left text-irongrey shadow-card transition-all duration-150 hover:-translate-y-0.5 hover:border-border-strong hover:bg-muted active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
  routeCanvasQuickActionBadgeClassName:
    "truncate text-[11px] text-text-tertiary",
  routeCanvasRecentHeadingClassName:
    "text-[11px] font-medium uppercase tracking-wider text-text-secondary",
  routeCanvasRecentItemClassName:
    "group flex w-full items-center gap-3 rounded-xl border border-border px-3 py-2.5 text-left text-irongrey transition-all hover:border-border-strong hover:bg-surface-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
  routeCanvasRecentAccentClassNames: [
    "bg-primary",
    "bg-aquamarine",
    "bg-vibrant-coral",
  ],
  routeCanvasRecentTimestampClassName:
    "whitespace-nowrap text-[10px] text-text-tertiary",
  routeCanvasFooterClassName:
    "text-[9px] text-text-tertiary",
  contentRailClassName: "mx-auto w-full min-w-0 max-w-[720px]",
  promptBannerShellClassName: "bg-surface-light px-4 pb-2 pt-2.5",
  promptBannerClassName:
    "mx-auto w-full max-w-[720px] rounded-[8px] bg-primary px-4 py-3 font-body text-[12px] font-medium text-white",
  chatPaneClassName: "bg-surface-light text-irongrey",
  sidePaneClassName: "bg-background-light text-irongrey",
  splitDividerClassName: "order-2 w-px shrink-0 bg-border",
  quickActionsHeadingClassName:
    "font-heading text-[13px] font-semibold text-irongrey",
  quickActionsScrollButtonClassName:
    "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-transparent bg-transparent text-text-secondary transition-colors hover:bg-muted hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25",
  quickActionCardClassName: ({
    surface,
    isPrototypeMinimalChrome,
  }: QuickActionCardOptions): string =>
    [
      surface === "navigate"
        ? `group relative flex w-full items-start gap-3 overflow-hidden rounded-[20px] px-3.5 py-3.5 text-left ${assistantCardBase}`
        : `group relative flex ${surface === "sticky" ? "min-h-[58px] rounded-[8px] px-2 py-2" : isPrototypeMinimalChrome ? "min-h-[80px] rounded-[8px] px-2 py-2" : "min-h-[110px] rounded-xl px-3 py-3"} w-full flex-col justify-between overflow-hidden text-left ${assistantCardBase}`,
    ].join(" "),
  quickActionLabelClassName: (isPrototypeMinimalChrome: boolean): string =>
    `${isPrototypeMinimalChrome ? "text-[10px]" : "text-[12px]"} font-heading font-semibold leading-snug text-irongrey [overflow-wrap:anywhere]`,
  quickActionIconClassName: (isPrototypeMinimalChrome: boolean): string =>
    `mt-2 flex shrink-0 items-center justify-center border border-border bg-muted text-text-secondary group-hover:border-primary/40 group-hover:bg-primary/10 group-hover:text-primary ${isPrototypeMinimalChrome ? "h-5 w-5 rounded-[6px]" : "h-7 w-7 rounded-lg"}`,
  quickActionNavigateIconClassName:
    "flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-primary/10 text-primary",
  quickActionNavigateBadgeClassName:
    "rounded-full border border-border bg-background-light px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-irongrey/58",
  quickActionHoverLineClassName:
    "pointer-events-none absolute inset-x-3 top-0 h-px bg-gradient-to-r from-transparent via-primary/25 to-transparent opacity-0 group-hover:opacity-100",
  recentItemClassName:
    "group flex w-full items-center gap-2.5 rounded-lg px-1 py-2 text-left text-irongrey hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
  recentItemSubtitleClassName: "truncate text-[10px] text-text-tertiary",
  emptyIntroClassName: (isPrototypeMinimalChrome: boolean): string =>
    isPrototypeMinimalChrome
      ? "overflow-hidden border-l-[3px] border-aquamarine px-3 py-1.5"
      : "overflow-hidden rounded-r-lg border-l-[3px] border-aquamarine bg-aquamarine/10 px-3 py-2.5",
  emptyFloorIconClassName: "text-border-strong",
  emptyFloorSparkleClassName: "absolute -right-1 -top-1 text-primary",
  toolbarActionClassName: (active: boolean): string =>
    [
      "group relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
      active ? "border-primary/15 bg-primary/10 text-primary" : assistantSoftControl,
    ].join(" "),
  groundingToggleClassName: (expanded: boolean): string =>
    [
      "group relative inline-flex h-9 shrink-0 items-center gap-2 overflow-hidden rounded-full px-1.5 pr-3",
      expanded ? "border border-primary/15 bg-primary/10 text-primary" : assistantSoftControl,
    ].join(" "),
  tabButtonClassName: (active: boolean): string =>
    [
      "inline-flex min-h-[30px] items-center justify-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium",
      active
        ? "border-primary/15 bg-primary/10 text-primary"
        : "border-border bg-transparent text-irongrey/54 hover:border-border-strong hover:bg-muted hover:text-irongrey/78",
    ].join(" "),
  headerActionClassName: `h-8 w-8 ${assistantSoftControl}`,
  headerModelPillClassName:
    "inline-flex h-8 max-w-[144px] items-center gap-2 rounded-full border border-border bg-background-light px-3 text-[11px] font-semibold text-irongrey/72 hover:border-primary/60 hover:bg-primary/5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
  schoolHeaderClassName:
    "relative flex h-12 items-center justify-between border-b border-border bg-surface-light px-4 text-irongrey",
  schoolBackButtonClassName:
    "flex h-8 w-8 items-center justify-center rounded-full border border-border bg-muted text-irongrey/55 hover:border-primary hover:bg-primary/5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
  schoolHistoryShortcutClassName:
    "inline-flex h-7 items-center gap-1 rounded-full px-1.5 text-[12px] font-medium text-irongrey/62 hover:bg-muted hover:text-irongrey focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
  contextMenuClassName:
    "absolute right-0 z-50 w-[280px] max-w-[calc(100vw-24px)] overflow-y-auto rounded-2xl border border-border bg-surface-light p-1 text-irongrey shadow-xl",
  closeButtonClassName:
    "flex h-10 w-10 items-center justify-center rounded-none border-0 bg-transparent text-irongrey/60 shadow-none hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
});
